/**
 * Did the server actually tell us this session's permissions? (HS-1387, C3)
 *
 * `GET /Auth/me` answers `Permissions: []` in two completely different situations, and gives the
 * client nothing to tell them apart:
 *
 * - the account genuinely holds no grants; or
 * - the backend declined to report them. `AuthFlowService.GetCurrentUserAsync` fills the list
 *   only when `!sessionId.HasValue || await HasScopeAccessAsync(institutionId, userId, sessionId)`,
 *   and for a token that carries a `session_id` but is not bound to a device, `HasScopeAccessAsync`
 *   returns `false` for any non-`IsSystemUser`.
 *
 * The two cases are byte-identical on the wire — `CurrentUserInfo.Permissions` is declared
 * `List<string> Permissions { get; set; } = new List<string>()`, so a withheld list serialises as
 * `[]`, never as `null`. The client cannot ask the question directly.
 *
 * What it CAN do is read the one asymmetry in that condition: with no `sessionId` the list is
 * always filled, so an empty list is the truth. Only when a `sessionId` IS present can an empty
 * list be a withholding.
 *
 * ⚠️ This is therefore a HEURISTIC, and it errs on the side of saying "not reported" — a session
 * that carries a `sessionId` and genuinely holds no grants is indistinguishable from one whose
 * list was withheld, and both are reported the same way here. That imprecision is the backend's
 * to remove: HS-1386 is where an explicit signal belongs. Until it exists, over-reporting is the
 * safer direction, because the alternative is an app that renders empty with no explanation at
 * all — which is the defect this function exists to end.
 */

import type { Session } from './machine';

/**
 * Whether the permission list on `session` can be trusted to be the whole truth.
 *
 * `true` for every session that carries at least one permission — a non-empty list was plainly
 * reported — and for every session with no `sessionId`, whose empty list is genuine.
 */
export function permissionsReported(session: Session): boolean {
  if ((session.permissions ?? []).length > 0) return true;
  return session.sessionId === undefined;
}
